// frontend/src/components/billing/plans-section.tsx
// Read DESIGN.md before modifying this file.
// Phase 1 split (spec 2026-06-18): the capacity & spend surface; the v2 plan
// view (V2PlansSection), overflow, and top-up. AIT-572 retired the legacy
// message-plan grid: no org is on it and nothing sells it anymore.
// Billing-account surfaces (current-plan info, saved card, invoices) live in
// billing-section.tsx. Endpoints: /organizations/:orgId/billing/* (subscription,
// checkout, usage-tier, and the add-card dialog's setup-intent +
// payment-method). The Stripe Customer Portal is retired.
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useWorkspace } from '@/contexts/workspace-context';
import { getV2Plan } from '@/lib/plans';
import { OverflowSwitch } from '@/components/billing/overflow-switch';
import { TopUpWallet } from '@/components/billing/topup-wallet';
import { ManageSubscriptionDialog } from '@/components/billing/manage-subscription-dialog';
import { AddCardDialog } from '@/components/billing/add-card-dialog';
import {
  useCheckout,
  useEligibilityQuery,
  useLatestInvoiceUrl,
  useOrgUsageQuery,
  useSubscriptionQuery,
  useSubscriptionInvalidate,
  useSwapTrialPlan,
  useUsageTierChange,
  useUsageTierPreview,
} from '@/components/billing/queries';
import { V2PlansSection } from '@/components/billing/v2-plans-section';
import { isValidPublicId } from '@/lib/public-id';
import { emit } from '@/lib/analytics';

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(iso));
}

export function PlansSection() {
  // Analytics-only: the checkout funnel events are workspace-keyed, so their
  // emits stay best-effort on the active workspace. Rendering and refetching
  // never depend on it — the page renders purely from the URL's org (spec D5).
  const { activeWorkspace } = useWorkspace();
  const { orgId } = useParams<{ orgId: string }>();
  const validOrgId = orgId && isValidPublicId(orgId, 'org') ? orgId : null;
  const subscriptionQuery = useSubscriptionQuery(validOrgId);
  const invalidateSubscription = useSubscriptionInvalidate(validOrgId);
  const checkout = useCheckout(validOrgId);
  const tierChange = useUsageTierChange(validOrgId);
  // AIT-420 Task 8 — the A2 state-matrix actions: in-trial plan swap and
  // pay-the-open-invoice. AIT-505 — paused recovery moved off Checkout onto
  // the native AddCardDialog (below); the resume itself is server-side.
  const swapTrialPlan = useSwapTrialPlan(validOrgId);
  const latestInvoiceUrl = useLatestInvoiceUrl(validOrgId);
  const subscription = subscriptionQuery.data ?? null;
  // Any in-flight fetch shows the loading state — first load AND every
  // refetch, matching the manual load() that always flipped loading on.
  const loading = subscriptionQuery.isPending || subscriptionQuery.isFetching;
  const error = subscriptionQuery.isError;
  const [checkoutSlug, setCheckoutSlug] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('annual');
  const awaitingExternalReturnRef = useRef(false);
  const [manageOpen, setManageOpen] = useState(false);
  // Native add-card dialog (Elements). Opened by the OverflowSwitch CTA for
  // card-less orgs; onSaved refetches the subscription so hasCard flips and
  // the pending overflow choice applies.
  const [addCardOpen, setAddCardOpen] = useState(false);
  // AIT-420 — a trialing card-less org that clicks Upgrade/Downgrade has
  // already made its choice; adding the card is the confirmation, not a
  // second prompt. Remembers which plan was clicked so onSaved below can
  // apply it immediately, with no second click and no switch-confirm dialog.
  // Cleared whenever the dialog closes without a successful save.
  const [pendingCardPlan, setPendingCardPlan] = useState<'build' | 'scale' | 'business' | null>(null);
  // AIT-494 -- set by onSaved before the dialog's close callback runs (refs are
  // synchronous where batched state is not), so the close handler can tell a
  // successful save from a dismissal that silently drops the plan pick.
  const cardSavedRef = useRef(false);
  // AIT-505 (review F12) — explicit state set by whichever opener flips
  // addCardOpen, never inferred later from subscription status: the paused
  // Alert's own "Add a credit card" button is 'alert', every plan-grid pick
  // (trial or paused) and the overflow-switch prompt are 'plans'.
  const [addCardSource, setAddCardSource] = useState<'alert' | 'plans' | null>(null);

  // Pending tier switch awaiting the confirm modal. `direction` drives the copy
  // (Upgrade = immediate prorated charge; Downgrade = scheduled at period end).
  // AIT-420: slug widened past the legacy four so the v2 build -> scale switch
  // goes through this SAME approval modal (preview -> confirm -> charge). A
  // prorated charge must never fire on one click.
  const [pendingTier, setPendingTier] = useState<
    // interval 'annual' marks the "Save 17%" monthly -> yearly switch: same
    // plan, immediate anchor-reset charge, its own dialog copy.
    { slug: string; name: string; direction: 'Upgrade' | 'Downgrade'; interval?: 'annual' } | null
  >(null);

  // Follow the subscription's persisted cadence on every fetch (keyed on
  // dataUpdatedAt so even a refetch returning identical data re-syncs the
  // tab, like the old load() did).
  useEffect(() => {
    const bi = subscriptionQuery.data?.billingInterval;
    if (bi) setBillingInterval(bi);
  }, [subscriptionQuery.data, subscriptionQuery.dataUpdatedAt]);

  // AIT-505 (review F12) — ONE emit site for the whole page: fires whenever
  // the dialog transitions to open, using whatever `addCardSource` the opener
  // set beforehand. Every opener below sets both in the same handler.
  useEffect(() => {
    if (addCardOpen && addCardSource) emit('add_card_opened', { source: addCardSource });
  }, [addCardOpen, addCardSource]);

  // Proration preview for the upgrade confirm dialog: the exact amount we'll
  // charge now. Fetched fresh whenever an upgrade dialog opens; downgrades
  // charge nothing now, so they skip (reset) the preview. While pending, the
  // dialog shows a spinner; a failure falls back to generic copy; a number is
  // the exact cents due today (0 = no charge now).
  const previewMutation = useUsageTierPreview(validOrgId);
  const { mutate: mutatePreview, reset: resetPreview } = previewMutation;
  useEffect(() => {
    if (!pendingTier || pendingTier.direction !== 'Upgrade') {
      resetPreview();
      return;
    }
    mutatePreview({ planSlug: pendingTier.slug, billingInterval: pendingTier.interval ?? billingInterval });
  }, [pendingTier, billingInterval, mutatePreview, resetPreview]);
  const preview: 'loading' | 'error' | number = previewMutation.isPending
    ? 'loading'
    : previewMutation.data &&
        !previewMutation.data.scheduled &&
        typeof previewMutation.data.amountDueCents === 'number'
      ? previewMutation.data.amountDueCents
      : 'error';

  // Org-pooled usage meter: total messages across every workspace in the org
  // vs the org's plan quota. Uses the same endpoint as the org sidebar meter
  // so both surfaces always show the same authoritative number.
  const orgUsageQuery = useOrgUsageQuery(validOrgId);
  const orgUsage = orgUsageQuery.data ?? null;


  // Automated billing and the top-up wallet are what happens when a PAID plan
  // runs out of actions, so for a v2 org they only mean anything once the
  // trial has converted. During the trial there is no bill to automate: the
  // plan is provisional, nothing is charged until the trial ends, and showing
  // overflow controls there just asks the customer to configure a decision
  // they have not made yet.
  const showsAutomatedBilling = Boolean(subscription?.hasEverPaid);

  const hasActiveSub =
    subscription &&
    subscription.plan.slug !== 'free' &&
    ['active', 'past_due', 'trialing'].includes(subscription.status);

  // Eligibility only ever NAMES a plan to buy, so fetch it only where something
  // is for sale: a trialing org (the in-trial downgrade offer) and an org whose
  // plan ended (canceled/incomplete_expired — the plan-grid recommendation).
  // Everywhere else the subscription payload already carries everything
  // rendered.
  const needsEligibility =
    !!subscription &&
    (subscription.status === 'trialing' ||
      subscription.status === 'canceled' ||
      subscription.status === 'incomplete_expired');
  const eligibilityQuery = useEligibilityQuery(validOrgId, needsEligibility);

  // What a saved card means for this customer's money, in their words. The
  // two cases differ, and a card that quietly becomes a charge is the thing
  // this text exists to prevent.
  const trialEndsAt = subscription?.trial?.endsAt ?? null;
  const trialEndLabel = trialEndsAt
    ? new Date(trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
    : null;
  const addCardNotice = (() => {
    // AIT-505 — paused: the exact plan/price a card save resumes onto. The
    // backend's resumePreview quotes the usage-earned plan; a "Resume on X"
    // grid pick (pendingCardPlan) overrides the name/price shown here with
    // that plan's own price at the SAME cadence the mint sends
    // (billingInterval), since that pick is what the mintVars/charge use.
    if (subscription?.status === 'paused') {
      const preview = subscription.resumePreview;
      const picked = pendingCardPlan ? getV2Plan(pendingCardPlan) : null;
      const name = picked?.name ?? preview?.name;
      const priceCents = picked
        ? billingInterval === 'annual'
          ? picked.annualPriceInCents
          : picked.priceInCents
        : preview?.priceCents;
      // Review round 3 fix (P2) — when the price lookup fails (no
      // resumePreview and no plan picked), fall back to generic truthful
      // copy with NO amount, never silently drop the notice: the default
      // "You won't be charged until you turn on automatic billing." copy in
      // add-card-dialog.tsx is wrong for paused, where saving resumes the
      // plan and charges it immediately.
      if (!name || priceCents == null) {
        return subscription.hasEverPaid
          ? 'Your subscription is paused. Saving a card resumes your plan and charges it now.'
          : 'Your trial ended. Saving a card resumes your plan and charges it now.';
      }
      const price = Math.round(priceCents / 100);
      return subscription.hasEverPaid
        ? `Your subscription is paused. Saving a card resumes ${name} and charges $${price} now.`
        : `Your trial ended. Saving a card resumes ${name} and charges $${price} now.`;
    }
    if (subscription?.status !== 'trialing') return undefined;
    const picked = pendingCardPlan ? getV2Plan(pendingCardPlan) : null;
    if (picked) {
      // The amount and cadence the customer is actually authorizing. The pick
      // carries billingInterval, and the toggle DEFAULTS to Yearly, so quoting
      // the monthly price here promised $24 while Stripe would charge $240
      // (Codex, PR #218).
      const isAnnual = subscriptionQuery.data?.billingInterval === 'annual';
      const price = Math.round((isAnnual ? picked.annualPriceInCents : picked.priceInCents) / 100);
      const forPlan = isAnnual ? `${picked.name} for a year` : picked.name;
      return trialEndLabel
        ? `We will charge you $${price} for ${forPlan} on ${trialEndLabel}, when your trial ends. Nothing is charged today.`
        : `We will charge you $${price} for ${forPlan} when your trial ends. Nothing is charged today.`;
    }
    const earned = eligibilityQuery.data?.eligiblePlan
      ? getV2Plan(eligibilityQuery.data.eligiblePlan)
      : null;
    // Quote the cadence the trial will actually convert on — an annual trial
    // charges the annual amount, not 12 monthly ones (CodeRabbit PR #260).
    const earnedIsAnnual = subscriptionQuery.data?.billingInterval === 'annual';
    const earnedPrice = earned
      ? earnedIsAnnual
        ? `$${Math.round(earned.annualPriceInCents / 100)}/year`
        : `$${Math.round(earned.priceInCents / 100)}/month`
      : '';
    const earnedPart = earned ? `, which is currently ${earned.name} at ${earnedPrice},` : '';
    return trialEndLabel
      ? `When your trial ends on ${trialEndLabel}, you will be charged for the relevant plan based on your usage${earnedPart} unless you choose otherwise.`
      : `When your trial ends, you will be charged for the relevant plan based on your usage${earnedPart} unless you choose otherwise.`;
  })();

  // A running trial is exempt from the quota machinery (no top-up/overflow to
  // configure until it converts) — Task 8: keyed off the Stripe-native


  // Refetch ONLY when the tab regains focus AFTER the user opened Stripe
  // Checkout. Prevents refetching on every alt-tab.
  useEffect(() => {
    function onVisible() {
      if (
        document.visibilityState === 'visible' &&
        awaitingExternalReturnRef.current
      ) {
        awaitingExternalReturnRef.current = false;
        void invalidateSubscription();
      }
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    if (!billing) return;

    const cleanUrl = () => {
      params.delete('billing');
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '');
      window.history.replaceState({}, '', newUrl);
    };

    if (billing === 'success') {
      toast.success('Subscription activated!');
      // Phase 125-06 -- emit plan_checkout_completed at the ?billing=success
      // moment (Stripe redirect landed on this page). The `plan` field is
      // best-effort: if `subscription` has already resolved we use its
      // `planSlug`; otherwise fall back to 'unknown' so the funnel
      // captures the redirect NOW without waiting for an async refetch.
      // workspace_id resolves from the activeWorkspace context.
      if (activeWorkspace?.id) {
        emit('plan_checkout_completed', {
          workspace_id: activeWorkspace.id,
          plan: subscription?.plan.slug ?? 'unknown',
        });
      }
      cleanUrl();
      void invalidateSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleCheckout(slug: string) {
    emit('plan_checkout_started', { plan_slug: slug, billing_interval: billingInterval, workspace_id: activeWorkspace?.id });
    setCheckoutSlug(slug);
    checkout.mutate(
      { planSlug: slug, billingInterval },
      {
        onSuccess: (data) => {
          awaitingExternalReturnRef.current = true;
          window.open(data.url, '_blank');
        },
        onError: (err) =>
          toast.error(err.userMessage ?? 'Something went wrong. Please try again.'),
        onSettled: () => setCheckoutSlug(null),
      },
    );
  }

  // AIT-420 Task 8 — in-trial plan swap: same subscription, no proration,
  // instant. Reuses the checkout spinner slot (checkoutSlug) so the clicked
  // card shows the same loading affordance.
  function handleSwapTrialPlan(slug: 'build' | 'scale' | 'business') {
    setCheckoutSlug(slug);
    // The subscription's OWN cadence, never the local toggle state. A trialing
    // org has hasActiveSub true, so the Monthly/Yearly tabs are not rendered
    // for it -- there is no selection to honour. The toggle still holds its
    // initial 'annual' until the fetch syncs it, and `billingInterval` is
    // undefined whenever the live Stripe read failed, so sending the toggle
    // could install the ANNUAL price on a monthly trial off a transient
    // outage (Codex, PR #218). Omitted when unknown; the DTO defaults to
    // monthly. Letting a trial CHOOSE annual is AIT-432.
    const knownInterval = subscriptionQuery.data?.billingInterval;
    swapTrialPlan.mutate(
      { slug, ...(knownInterval ? { interval: knownInterval } : {}) },
      {
        onSuccess: () => toast.success('Plan scheduled for when your trial ends.'),
        onError: (err) => toast.error(err.userMessage ?? 'Could not switch plans. Please try again.'),
        onSettled: () => setCheckoutSlug(null),
      },
    );
  }

  // AIT-420 Task 8 — past_due/unpaid: open the hosted invoice Stripe already
  // generated. Paying it there flows back to us through invoice.paid.
  function handlePayInvoice() {
    latestInvoiceUrl.mutate(undefined, {
      onSuccess: (data) => {
        awaitingExternalReturnRef.current = true;
        window.open(data.url, '_blank');
      },
      onError: (err) =>
        toast.error(err.userMessage ?? 'Could not load your invoice. Please try again.'),
    });
  }

  // AIT-420: widened from the legacy four so the v2 build -> scale switch
  // rides the SAME endpoint and invalidation. The backend is the single
  // authority on which move is legal (V2_PLAN_TIER_LOCKED otherwise).
  function handleUsageTierChange(slug: string, interval?: 'annual') {
    setCheckoutSlug(slug);
    tierChange.mutate(
      { planSlug: slug, billingInterval: interval ?? billingInterval },
      {
        onSuccess: (data) =>
          toast.success(
            interval === 'annual'
              ? 'You are on yearly billing now.'
              : data.scheduled
              ? 'Plan change scheduled for the end of your billing period.'
              : 'Plan updated.',
          ),
        onError: (err) =>
          toast.error(err.userMessage ?? 'Something went wrong. Please try again.'),
        onSettled: () => setCheckoutSlug(null),
      },
    );
  }

  return (
    <Card className="shrink-0">
      {subscription && !hasActiveSub && (
        <CardHeader>
          <CardTitle className="flex justify-end">
            <Tabs
              value={billingInterval}
              onValueChange={(v) => setBillingInterval(v as 'monthly' | 'annual')}
              className="w-full md:w-fit"
            >
              <TabsList className="grid w-full grid-cols-2 md:w-auto">
                <TabsTrigger
                  value="monthly"
                  className="px-6"
                  data-state={billingInterval === 'monthly' ? 'active' : 'inactive'}
                >
                  Monthly
                </TabsTrigger>
                <TabsTrigger
                  value="annual"
                  className="px-6"
                  data-state={billingInterval === 'annual' ? 'active' : 'inactive'}
                >
                  Yearly
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading billing info...
          </div>
        ) : error ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Unable to load billing info.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void subscriptionQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        ) : subscription ? (
          <>
            <V2PlansSection
              sub={subscription}
              eligibility={eligibilityQuery.data ?? null}
              trialDaysLeft={orgUsage?.trialDaysLeft ?? null}
              billingInterval={billingInterval}
              checkoutSlug={checkoutSlug}
              onCheckout={handleCheckout}
              onAddCard={(slug) => {
                setPendingCardPlan(slug ?? null);
                // AIT-505 (review F12) — the ONLY 'alert' source: the paused
                // Alert's own "Add a credit card" button (no slug picked).
                // Every plan-grid pick, trial or paused, is 'plans'.
                setAddCardSource(subscription.status === 'paused' && !slug ? 'alert' : 'plans');
                setAddCardOpen(true);
              }}
              onManage={() => setManageOpen(true)}
              onSwitchToAnnual={() =>
                setPendingTier({
                  slug: subscription.plan.slug,
                  name: `${subscription.plan.name} yearly`,
                  direction: 'Upgrade',
                  interval: 'annual',
                })
              }
              onUpgrade={(slug, name) => setPendingTier({ slug, name, direction: 'Upgrade' })}
              onSwapTrialPlan={handleSwapTrialPlan}
              onPayInvoice={handlePayInvoice}
            />

            {showsAutomatedBilling && (
              <OverflowSwitch
                orgId={validOrgId}
                sub={subscription}
                onAddCard={() => {
                  setAddCardSource('plans');
                  setAddCardOpen(true);
                }}
              />
            )}
            {showsAutomatedBilling && <TopUpWallet orgId={validOrgId} />}

            {/* Manage Subscription modal: the single cancellation surface
                (per-product cancel rows + "Cancel everything"). */}
            <ManageSubscriptionDialog
              orgId={orgId}
              billingInterval={billingInterval}
              open={manageOpen}
              onOpenChange={setManageOpen}
              // Downgrade hands off to the shared tier-switch approval modal
              // (period-end copy) instead of confirming inline.
              onDowngrade={(slug, name) => {
                setManageOpen(false);
                setPendingTier({ slug, name, direction: 'Downgrade' });
              }}
            />

          </>
        ) : null}
        {/* AIT-494 -- OUTSIDE the loading/error/subscription conditional: `loading`
            flips true on EVERY refetch (isPending || isFetching) and was unmounting
            these dialogs mid-flow (confirm dialog vanishing while its preview
            loaded; AddCardDialog losing step state on the onSaved refetch). */}
        {/* Tier-switch confirmation: explains the billing effect before the
            change is applied. Upgrade = immediate prorated charge; downgrade
            = scheduled at the end of the current period (current plan kept
            until then). */}
        {pendingTier && subscription && (
              <Dialog open onOpenChange={(open) => { if (!open) setPendingTier(null); }}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {pendingTier.interval === 'annual'
                        ? `Lock in ${pendingTier.name}`
                        : `${pendingTier.direction} to ${pendingTier.name}`}
                    </DialogTitle>
                    <DialogDescription>
                      {pendingTier.interval === 'annual' ? (
                        preview === 'loading' ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                            Calculating today's charge...
                          </span>
                        ) : typeof preview === 'number' ? (
                          <>
                            Pay for 10 months, get 2 free. You'll be charged $
                            {(preview / 100).toFixed(2)} now, a full year minus credit for the
                            unused part of this month. Two months on us, same access, one payment.
                          </>
                        ) : (
                          // Preview failed — Confirm stays disabled (no charge
                          // without the exact amount on screen), so say why.
                          <>
                            We could not calculate today's charge. Close this and
                            try again in a moment.
                          </>
                        )
                      ) : pendingTier.direction === 'Upgrade' ? (
                        preview === 'loading' ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                            Calculating today's charge...
                          </span>
                        ) : typeof preview === 'number' && preview > 0 ? (
                          <>
                            You'll switch to {pendingTier.name} right away. We'll charge $
                            {(preview / 100).toFixed(2)} today for the rest of this billing period
                            {subscription.currentPeriodEnd
                              ? ` (through ${formatDate(subscription.currentPeriodEnd)})`
                              : ''}
                            . {subscription.currentPeriodEnd
                              ? `On ${formatDate(subscription.currentPeriodEnd)} your next bill is the full ${pendingTier.name} price.`
                              : `Your next bill is the full ${pendingTier.name} price.`}
                          </>
                        ) : typeof preview === 'number' ? (
                          <>
                            You'll switch to {pendingTier.name} right away at no extra charge today.
                            {subscription.currentPeriodEnd
                              ? ` On ${formatDate(subscription.currentPeriodEnd)} your next bill is the full ${pendingTier.name} price.`
                              : ` Your next bill is the full ${pendingTier.name} price.`}
                          </>
                        ) : (
                          <>
                            You'll switch to {pendingTier.name} right away. Today you only pay the
                            price difference for the days left in this billing period. Your next bill
                            is the full {pendingTier.name} price.
                          </>
                        )
                      ) : (
                        <>
                          You'll keep {subscription.plan.name} and its usage until{' '}
                          {subscription.currentPeriodEnd
                            ? formatDate(subscription.currentPeriodEnd)
                            : 'the end of your billing period'}
                          . {pendingTier.name} starts then, and nothing is charged now.
                        </>
                      )}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPendingTier(null)}>
                      Cancel
                    </Button>
                    <Button
                      // Annual = an immediate full-year charge, so the exact
                      // amount MUST be on screen before Confirm is clickable
                      // (Codex PR #260). Tier upgrades keep the generic-copy
                      // fallback: their proration is small and period-bounded.
                      disabled={
                        pendingTier.direction === 'Upgrade' &&
                        (pendingTier.interval === 'annual'
                          ? typeof preview !== 'number'
                          : preview === 'loading')
                      }
                      onClick={() => {
                        const { slug, interval } = pendingTier;
                        setPendingTier(null);
                        handleUsageTierChange(slug, interval);
                      }}
                    >
                      {pendingTier.interval === 'annual' ? 'Confirm Switch' : `Confirm ${pendingTier.direction}`}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
        {validOrgId && (
          <AddCardDialog
            orgId={validOrgId}
            notice={addCardNotice}
            collectPhone
            open={addCardOpen}
            mintVars={{
              targetSlug: pendingCardPlan ?? undefined,
              // AIT-505 — paused + no plan picked: the notice above quotes
              // the server's resumePreview (its own, live billingInterval),
              // not the page's Monthly/Yearly toggle. Send the same cadence
              // here or the modal can quote one interval while Stripe
              // charges another (mirrors billing-method.tsx's mintVars).
              interval:
                subscription?.status === 'paused' && !pendingCardPlan
                  ? (subscription?.resumePreview?.interval ?? billingInterval)
                  : billingInterval,
              source: addCardSource ?? 'plans',
            }}
            onOpenChange={(open) => {
              setAddCardOpen(open);
              // Closed without saving (backdrop/Escape/Cancel) -- forget
              // the remembered plan so a later, unrelated card add never
              // fires a stale plan change. A successful save's onSaved
              // below reads pendingCardPlan from this SAME render before
              // this clear takes effect (React batches both calls from
              // AddCardDialog's onDone), so the apply still happens.
              // AIT-494 -- and SAY SO: real customers closed this dialog
              // believing their pick took effect and were left on the
              // trial's Business default. A save-close runs onSaved first
              // (AddCardDialog's onDone order) which flips the ref, so the
              // toast only fires for a close that really dropped the pick.
              if (!open && pendingCardPlan && !cardSavedRef.current) {
                toast.info('Your plan was not changed.');
              }
              if (!open) {
                cardSavedRef.current = false;
                setPendingCardPlan(null);
                setAddCardSource(null);
              }
            }}
            onSaved={() => {
              cardSavedRef.current = true;
              // AIT-420 — a TRIALING plan click was the intent, adding the
              // card is the confirmation: apply it now, the SAME call the
              // confirm-dialog path makes, no second click. Never fired for
              // paused: the trial-plan endpoint only accepts a trialing sub.
              if (subscription?.status === 'trialing' && pendingCardPlan) {
                const slug = pendingCardPlan;
                setPendingCardPlan(null);
                handleSwapTrialPlan(slug);
              } else if (pendingCardPlan) {
                setPendingCardPlan(null);
              }
            }}
          />
        )}
      </CardContent>

    </Card>
  );
}
